<template>
  <MembersHero />
  <div class="members-loyalty">
    <SectionBlock
      :title="earnRateTitle"
      :sub-title="howItWorksSubTitle"
      with-border
    >
      <ActionBlocks
        v-if="howItWorksBlocks"
        :custom-blocks="howItWorksBlocks"
        plain
      />
    </SectionBlock>

    <SectionBlock
      title="Level Benefits"
      sub-title="Unlock benefits as you move through the levels."
      with-border
    >
      <LoyaltyTable />
    </SectionBlock>
    <SectionBlock
      class="earn-rewards"
      title="EARN REWARDS"
      sub-title="Earn rewards with every purchase and special activities."
      with-border
    >
      <ActionBlocks :custom-blocks="earnRewardsBlocks" rewards />
    </SectionBlock>
  </div>

  <section class="loyalty-faq-banner">
    <div class="loyalty-faq-banner__inner">
      <Media
        :images="faqBannerImages"
        cover
        cover-position="center center"
        no-transition
        eager
      />
    </div>
  </section>

  <div class="members-loyalty">
    <SectionBlock
      title="FREQUENTLY ASKED QUESTIONS"
      sub-title="Everything to know about Members Only."
      with-border
    >
      <FrequentlyAskedQuestions />
    </SectionBlock>
  </div>

  <section class="loyalty-bestsellers">
    <HomepageRecommendationsCarousel
      title="BESTSELLERS"
      :subtitle="bestsellersCarouselSubtitle"
      link="/collections/best-sellers"
      collection-handle="best-sellers"
      collection-only
      slide-width="310px"
      slide-width-tablet="310px"
      slide-width-desktop="300px"
    />
  </section>

  <MembersFooter @speak-to-us="handleSpeakToUs" />
</template>

<script>
import { mapState, mapGetters } from "vuex";
import MembersHero from "@/components/account/MembersHero.vue";
import ActionBlocks from "@/components/ActionBlocks.vue";
import SectionBlock from "@/components/SectionBlock.vue";
import LoyaltyTable from "@/components/account/loyalty/LoyaltyTable.vue";
import MembersFooter from "@/components/account/MembersFooter.vue";
import FrequentlyAskedQuestions from "@/components/account/FrequentlyAskedQuestions.vue";
import { businessLogic } from "@/config.js";
import { getSignUpBonus } from "@/config";
import { getFeatureToggles } from "@/helpers/featureToggles.js";
import loyaltyTiers from "@/assets/json/loyalty-tiers.json";
import howItWorksEmailIcon from "@/assets/icons/email-at2.png";
import howItWorksTagsIcon from "@/assets/icons/tags2.png";
import howItWorksBasketIcon from "@/assets/icons/basket-plus2.png";
import earnRewardsBagIcon from "@/assets/images/earn-rewards/bag.svg";
import earnRewardsGiftIcon from "@/assets/images/earn-rewards/gift.svg";
import earnRewardsHeartIcon from "@/assets/images/earn-rewards/heart.svg";
import Media from "@/components/Media.vue";
import HomepageRecommendationsCarousel from "@/components/homepage/HomepageRecommendationsCarousel.vue";

export default {
  name: "Loyalty",
  components: {
    MembersHero,
    SectionBlock,
    ActionBlocks,
    LoyaltyTable,
    MembersFooter,
    FrequentlyAskedQuestions,
    Media,
    HomepageRecommendationsCarousel,
  },
  data() {
    const faqBannerUrl =
      "https://cdn.shopify.com/s/files/1/0942/9062/files/Frame_2434_1.jpg?v=1778496578";
    return {
      loyaltyTiersJson: loyaltyTiers,
      loading: true,
      faqBannerImages: {
        smallMedia: faqBannerUrl,
        mediumMedia: faqBannerUrl,
        largeMedia: faqBannerUrl,
        altText: "Rat & Boa members",
      },
      loyaltyImages: {
        smallMedia:
          "https://cdn.shopify.com/s/files/1/0942/9062/files/Frame_2434_1_1.jpg?v=1774369180",
        mediumMedia:
          "https://cdn.shopify.com/s/files/1/0942/9062/files/Frame_2434_1_1.jpg?v=1774369180",
        largeMedia:
          "https://cdn.shopify.com/s/files/1/0942/9062/files/Frame_2434_2.jpg?v=1774369180",
      },
    };
  },
  computed: {
    ...mapState("auth", ["customer", "customerAccessToken"]),
    ...mapGetters("app", ["currencyCode"]),
    bonus() {
      if (!this.currencyCode) {
        return null;
      }

      const featureToggles = getFeatureToggles();
      const amount = getSignUpBonus(this.currencyCode, featureToggles);
      if (typeof amount === "string" && amount.endsWith("%")) {
        return amount;
      }
      return this.$filters.toCurrency(amount, this.currencyCode, 0);
    },
    earnRateTitle() {
      if (!this.currencyCode) {
        return "";
      }
      const oneUnit = this.$filters.toCurrency(1, this.currencyCode, 0);
      return `Spend ${oneUnit}, earn 1 point`;
    },
    howItWorksSubTitle() {
      return "Track your points and redeem at checkout.";
    },
    bestsellersCarouselSubtitle() {
      return "The hero pieces we can't live without.";
    },
    howItWorksBlocks() {
      if (!this.currencyCode) {
        return null;
      }
      const oneCredit = this.$filters.toCurrency(1, this.currencyCode, 0);
      return [
        {
          title: "SIGN UP",
          description: `Join Members Only today and receive ${this.bonus} credit to get started.`,
          icon: "email-at2",
          iconSrc: howItWorksEmailIcon,
        },
        {
          title: "EARN AS YOU SPEND",
          description:
            "Collect points with every purchase and unlock exclusive rewards.",
          icon: "tags2",
          iconSrc: howItWorksTagsIcon,
        },
        {
          title: "REDEEM AT CHECKOUT",
          description: `Exchange ${businessLogic.loyaltyPointsConversion} points for ${oneCredit} at checkout on any product of your choice.`,
          icon: "basket-plus2",
          iconSrc: howItWorksBasketIcon,
        },
      ];
    },
    earnRewardsBlocks() {
      if (!this.currencyCode) return;
      const unit = this.$filters.toCurrency(1, this.currencyCode, 0);
      const birthdayAmounts = this.loyaltyTiersJson.tiers.map(
        (tier) => tier.birthdayBonus?.[this.currencyCode] || 0
      );
      const birthday = this.$filters.toCurrency(
        Math.max(...birthdayAmounts),
        this.currencyCode,
        0
      );

      return [
        {
          title: "MAKE A PURCHASE",
          description: `Earn 1 point for every ${unit} spent`,
          iconSrc: earnRewardsBagIcon,
        },
        {
          title: "CELEBRATE A BIRTHDAY",
          description: `Celebrate with up to ${birthday}`,
          iconSrc: earnRewardsGiftIcon,
        },
        {
          title: "REFER A FRIEND",
          description: `Refer a friend: Gift ${this.bonus}, receive ${this.bonus}`,
          iconSrc: earnRewardsHeartIcon,
        },
      ];
    },
  },
  methods: {
    startShopping() {
      console.log("Start shopping");
    },
    handleSpeakToUs() {
      console.log("Speak to us clicked");
    },
  },
};
</script>

<style lang="scss" scoped>
.members-loyalty {
  @extend .containerV2;
  max-width: 1126px;
  margin: 0 auto;
  padding-top: 20px;

  @include respond-above(md) {
    padding-top: 40px;
  }

  &__title {
    @include editorial-headline(32px);
    text-transform: uppercase;
    color: $color-white;
  }

  &__points-text {
    @include editorial-headline(24px);
    color: $color-white;
    line-height: 110%;
    text-transform: uppercase;
    @include respond-above(md) {
      @include editorial-headline(32px);
    }
  }
}

.loyalty-faq-banner {
  padding: 0 20px 40px;

  @include respond-above(md) {
    padding: 0 40px 60px;
  }

  &__inner {
    max-width: 1200px;
    margin: 0 auto;
    overflow: hidden;

    :deep(.wrapper) {
      display: block;
      width: 100%;
    }

    :deep(.media.media--cover) {
      width: 100%;
      height: auto;
      aspect-ratio: 12 / 5;
      max-height: min(42vw, 480px);
      min-height: 180px;
    }

    :deep(.media.media--cover img) {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
}

.earn-rewards {
  :deep(h2.section-block__title) {
    font-family: $heading-font-family;
    font-weight: 100;
    font-style: normal;
    font-size: 24px;
    line-height: 100%;
    text-align: center;
    text-transform: uppercase;
    color: #000;
    margin-bottom: 16px;
  }

  :deep(h3.section-block__sub-title) {
    font-family: $utility-font-family;
    font-weight: 400;
    font-style: normal;
    font-size: 14px;
    line-height: 150%;
    text-align: center;
    color: #000;
    max-width: 660px;
    margin-bottom: 0;
  }
}

.loyalty-bestsellers {
  padding: 0 0 48px;

  @include respond-above(md) {
    padding-bottom: 80px;
  }
}
</style>
